// @ts-nocheck
import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Grid,
  Icon,
  TextField,
  Button,
  Typography,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useIfMounted } from '../../../../common/hooks';
import { DateUtils, StringUtils } from '../../../../common/utils';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '64px',
      '& .MuiFormHelperText-marginDense': {
        marginTop: '2px',
      },
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(1),
    overflow: 'auto',
  },
  formSection: {
    flex: 1,
    '&:first-child': {
      marginRight: theme.spacing(3),
    },
    '&:last-child': {
      flex: '1 1 100%',
    },
    '& .MuiTypography-gutterBottom': {
      marginBottom: '1em',
    },
    '& .MuiGrid-item': {
      padding: theme.spacing(0, 1),
    },
  },
  futureChanges: {
    '& .Mui-disabled': {
      '& fieldset.MuiOutlinedInput-notchedOutline': {
      borderColor: 'red',
      }
    },
    '& label.Mui-disabled': {
      color: 'red'
    },
    '& .MuiFormControlLabel-label': {
      color: 'red'
    }
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function ProductForm(props) {
  const {
    product,
    productChanges,
    onCancel,
    editMode,
    actionsDisabled,
    cancelButtonText,
    cancelButtonIcon,
  } = props;
  
  const [editable, setEditable] = useState(!editMode);
  const [t] = useTranslation();
  const { formState, errors, control, reset, register, handleSubmit } = useForm();
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const { isDirty, isSubmitting } = formState;

  const onSubmit = async data => {
    const formData = omitEmpty(data);
    if (product?.id) {
      formData.id = product.id;
    }
    try {
      await onSave(formData);
      ifMounted(() => {
        reset(data);
        if (editMode) {
          setEditable(false);
        }
      });
    } catch (e) {
      // Error already handled
    }
  };

  const handleCancel = () => {
    if (editMode && editable) {
      reset();
      setEditable(false);
    } else {
      onCancel();
    }
  };

  const futureChange = (name: string, money: boolean = false) => {
    if(!productChanges || productChanges.length === 0)
      return undefined;

    const changes = productChanges[0];
    if (changes.productChangeLines) {
      const latestEntry = changes.productChangeLines.find(change => change.type === name);
      if (latestEntry) {
        let text: string;
        const days = DateUtils.daysTill(changes.effectiveDate);
        if(days === 0) {
          const hours = DateUtils.hoursTill(changes.effectiveDate);
          if(hours === 0) {
            const minutes = DateUtils.minutesTill(changes.effectiveDate);
            text = StringUtils.getFormattedMinutes(minutes);
          } else {
            text = StringUtils.getFormattedHours(hours);
          }
        } else {
          text = StringUtils.getFormattedDays(days);
        }
        if([ "TITLE", "DESCRIPTION" ].includes(name)) {
          return ` - ${t('product.productChanges').toLocaleLowerCase()} ${t('common.in')} ${text}`;
        } else {
          return ` - ${money ? StringUtils.getFormattedSum(latestEntry.value) : latestEntry.value}
          ${t('common.in')} ${text}`;
        }
      }
    }
    return undefined;
  };

  const title = futureChange("TITLE");
  const description = futureChange("DESCRIPTION");
  const price = futureChange("PRICE", true);
  const freePeriod = futureChange("FREEPERIOD");
  const commitmentDays = futureChange("COMMITMENTDAYS");
  const quarantineDays = futureChange("QUARANTINEDAYS");
  const suspendFree = futureChange("SUSPENDFREE");

  const getLabel = (labelName: string, value: string | undefined) => `${t(labelName)}${value ?? ''}`;

  return (
    <Box id="ProductForm-container" className={classes.container}>
      <form id="ProductForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Box className={classes.formSection}>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <TextField
                  id="ProductForm-id"
                  name="id"
                  label={t('product.id')}
                  variant="outlined"
                  size="small"
                  defaultValue={product?.id}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  id="ProductForm-title"
                  name="title"
                  label={getLabel('product.productTitle', title)}
                  variant="outlined"
                  size="small"
                  defaultValue={product?.title}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    required: t('product.productTitleRequired'),
                  })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={!editable}
                  fullWidth
                  className={title ? classes.futureChanges : ''}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  id="ProductForm-price"
                  name="price"
                  label={getLabel('product.productPrice', price)}
                  variant="outlined"
                  size="small"
                  defaultValue={StringUtils.getFormattedSum(product?.price)}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  fullWidth
                  className={price ? classes.futureChanges : ''}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="ProductForm-freePeriod"
                  name="freePeriod"
                  label={getLabel('product.productFreePeriod', freePeriod)}
                  variant="outlined"
                  size="small"
                  defaultValue={StringUtils.getFormattedDays(product?.discountDays)}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  fullWidth
                  className={freePeriod ? classes.futureChanges : ''}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="ProductForm-commitmentDays"
                  name="commitmentDays"
                  label={getLabel('product.productCommitmentDays', commitmentDays)}
                  variant="outlined"
                  size="small"
                  defaultValue={StringUtils.getFormattedDays(product?.commitmentDays)}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  fullWidth
                  className={commitmentDays ? classes.futureChanges : ''}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="ProductForm-quarantineDays"
                  name="quarantineDays"
                  label={getLabel('product.productQuarantineDays', quarantineDays)}
                  variant="outlined"
                  size="small"
                  defaultValue={StringUtils.getFormattedDays(product?.discountEligibilityIntervalDays)}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  fullWidth
                  className={quarantineDays ? classes.futureChanges : ''}
                />
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  label={getLabel('product.productSuspendFree', suspendFree)}
                  labelPlacement="end"
                  className={suspendFree ? classes.futureChanges : ''}
                  control={
                    <Controller
                      name="suspendFree"
                      control={control}
                      defaultValue={_.defaultTo(product?.suspendFree, false)}
                      render={params => (
                        <Switch
                          id="ProductForm-suspendFree"
                          checked={params.value}
                          onChange={e => params.onChange(e.target.checked)}
                          disabled={!editable}
                        />
                      )}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="ProductForm-description"
                  name="description"
                  label={getLabel('product.productDescription', description)}
                  variant="outlined"
                  size="small"
                  defaultValue={product?.description}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  rows={4}
                  multiline
                  fullWidth
                  className={description ? classes.futureChanges : ''}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="ProductForm-cancel"
            className={classes.button}
            startIcon={<Icon>{!editable ? cancelButtonIcon : 'cancel'}</Icon>}
            onClick={handleCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {!editable ? cancelButtonText : t('common.cancel')}
            </Typography>
          </Button>
        </Box>
      </form>
    </Box>
  );
}

ProductForm.defaultProps = {
  product: undefined,
  productChanges: undefined,
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  actionsDisabled: undefined,
  editMode: undefined,
};

ProductForm.propTypes = {
  product: PropTypes.object.isRequired,
  productChanges: PropTypes.arrayOf(PropTypes.object),
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
};

export default ProductForm;
